import { OTPInput } from "../base-ui/OTPInput";
import { getDeeplink } from "../utils";
import { Button } from "./../base-ui/index";
import { TOTP_CODE_LENGTH } from "@/components/mfa/totp";
import { useTotpChallenge } from "@/components/mfa/useTotpChallenge";
import { safeAsync } from "@/lib/utils";
import { getSiteOrigin } from "@/utils/helpers";
import type { Database } from "@/utils/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { ArrowLeft } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import type { FormEvent, MouseEvent } from "react";

function MfaChallenge({
  supabaseClient,
}: {
  supabaseClient: SupabaseClient<Database>;
}) {
  const errorMessageId = "mfa-code-error";
  const router = useRouter();
  const searchParams = useSearchParams();
  const challenge = useTotpChallenge({
    supabase: supabaseClient,
    purpose: { kind: "challenge", context: "sign_in" },
    onVerified: () => {
      const deeplink = getDeeplink(
        searchParams.get("deeplink"),
        getSiteOrigin()
      );
      router.push(deeplink ?? "/dashboard");
      router.refresh();
    },
  });

  const signOut = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    await supabaseClient.auth.signOut({ scope: "local" });
    router.push("/auth");
    router.refresh();
  };

  return (
    <form
      id="auth-mfa-challenge"
      onSubmit={safeAsync(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (
          challenge.code.length !== TOTP_CODE_LENGTH ||
          challenge.isVerifying
        ) {
          return;
        }
        await challenge.verify();
      })}
    >
      <div className="flex flex-col gap-3 text-left">
        <p className="text-center text-sm text-content-tertiary">
          Enter the {TOTP_CODE_LENGTH}-digit code from your authenticator app.
        </p>
        <OTPInput
          value={challenge.code}
          onChange={challenge.setCode}
          length={TOTP_CODE_LENGTH}
          onComplete={
            challenge.requiresRetry
              ? undefined
              : (code) => void challenge.verify(code)
          }
          disabled={challenge.isVerifying}
          autoFocus
          error={Boolean(challenge.error)}
          errorMessageId={errorMessageId}
        />
        {challenge.error && (
          <p id={errorMessageId} role="alert" className="text-sm text-red-500">
            {challenge.error}
          </p>
        )}
        {challenge.requiresRetry && (
          <Button type="submit" color="primary" disabled={!challenge.canVerify}>
            {challenge.isVerifying ? "Retrying..." : "Retry"}
          </Button>
        )}
        <a
          href="/auth?view=sign_in"
          onClick={safeAsync(signOut)}
          className="mt-3 inline-flex items-center justify-center gap-1 text-sm text-blue-500 hover:text-blue-600"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </a>
      </div>
    </form>
  );
}

export { MfaChallenge };
